import {
    PairFacts,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {
    alignToNearerEdge,
    getBoxDistance,
    PlanAxis,
} from 'components/customer/RoomPlanner/helpers/calculations';
import {Box3, Group, Mesh, Object3D, Object3DEventMap, Vector3} from 'three';
import {
    getRoomInteriorBounds,
    isFootprintInsideRoom,
} from 'components/customer/RoomPlanner/helpers/wallProximity';
import {toCandidate} from 'components/customer/RoomPlanner/helpers/snapping/strategies/candidate';
import {
    FACE_SIDE_TOLERANCE,
    SHAPE_SNAP_DISTANCE,
} from 'components/customer/RoomPlanner/helpers/snapping/constants';

interface ShapeSnapCandidate {
    dist: number;
    x: number;
    z: number;
}

// Proximity-based face-to-face snap for L/Key/U panels. A face pair is offered
// only when the panel is on that side of the target and the resulting pose fits
// between the walls, so a target flush in a corner cannot offer its wall face.
const findLShapePanelSnap = (
    cabinetGroup: Group<Object3DEventMap>,
    otherProduct: Group<Object3DEventMap>,
    pair: PairFacts,
    targetPos: Vector3,
    currentPos: Vector3,
    wallGroup?: Object3D | null
): Vector3 | null => {
    const selectedBox = pair.selected.box;
    const otherBox = pair.other.box;
    const delta = targetPos.clone().sub(currentPos);
    const shiftedBox = selectedBox.clone().translate(delta);

    const boxDist = getBoxDistance(shiftedBox, otherBox);

    if (boxDist.x >= SHAPE_SNAP_DISTANCE && boxDist.z >= SHAPE_SNAP_DISTANCE) {
        return null;
    }

    const aligned = (axis: PlanAxis) =>
        alignToNearerEdge(axis, shiftedBox, otherBox, targetPos);

    const candidates: ShapeSnapCandidate[] = [];

    if (boxDist.z < SHAPE_SNAP_DISTANCE) {
        if (shiftedBox.max.x <= otherBox.min.x + FACE_SIDE_TOLERANCE) {
            candidates.push({
                dist: Math.abs(shiftedBox.max.x - otherBox.min.x),
                x: targetPos.x + (otherBox.min.x - shiftedBox.max.x),
                z: aligned('z'),
            });
        }
        if (shiftedBox.min.x >= otherBox.max.x - FACE_SIDE_TOLERANCE) {
            candidates.push({
                dist: Math.abs(shiftedBox.min.x - otherBox.max.x),
                x: targetPos.x + (otherBox.max.x - shiftedBox.min.x),
                z: aligned('z'),
            });
        }
    }

    if (boxDist.x < SHAPE_SNAP_DISTANCE) {
        if (shiftedBox.max.z <= otherBox.min.z + FACE_SIDE_TOLERANCE) {
            candidates.push({
                dist: Math.abs(shiftedBox.max.z - otherBox.min.z),
                x: aligned('x'),
                z: targetPos.z + (otherBox.min.z - shiftedBox.max.z),
            });
        }
        if (shiftedBox.min.z >= otherBox.max.z - FACE_SIDE_TOLERANCE) {
            candidates.push({
                dist: Math.abs(shiftedBox.min.z - otherBox.max.z),
                x: aligned('x'),
                z: targetPos.z + (otherBox.max.z - shiftedBox.min.z),
            });
        }
    }

    const interior = getRoomInteriorBounds(
        wallGroup ?? (cabinetGroup.userData.currentWall as Mesh | null)?.parent
    );

    const footprint = new Box3();
    const move = new Vector3();
    let best: ShapeSnapCandidate | null = null;

    for (const candidate of candidates) {
        if (candidate.dist >= SHAPE_SNAP_DISTANCE) continue;
        if (best && candidate.dist >= best.dist) continue;

        if (interior) {
            move.set(candidate.x - targetPos.x, 0, candidate.z - targetPos.z);
            const fits = isFootprintInsideRoom(
                footprint.copy(shiftedBox).translate(move),
                interior
            );
            if (!fits) continue;
        }

        best = candidate;
    }

    if (!best) return null;

    return new Vector3(best.x, targetPos.y, best.z);
};

// L-shaped and key-shaped panels, and L-shaped corners against plain
// cabinets: snaps by bounding-box face proximity.
export const getLShapePanelSnap: SnapStrategy = (
    cabinetGroup,
    otherProduct,
    ctx,
    pair
) =>
    toCandidate(
        findLShapePanelSnap(
            cabinetGroup,
            otherProduct,
            pair,
            ctx.targetPos,
            ctx.currentPos,
            ctx.wallGroup
        ),
        ctx.targetPos
    );
